// API for the Safety pages — Safety Overview, Safety Concerns, Fire
// Extinguishers, Training Videos, Toolbox Talk Results (Admin / Safety
// Manager only). Every endpoint identifies the caller by the x-user-id
// header (no token validation server-side — the same trust model as the
// Fleet and PCW admin routes). Servers: server/routes/api/Safety/. The data
// itself comes from the mobile app.

export const SAFETY_ROLES = ["Admin", "Safety Manager"];

// Which upload goes where: a toolbox talk is open to everyone; a training
// video only reaches the people it's assigned to.
export const VIDEO_KINDS = {
  toolbox: { label: "Toolbox talk", noun: "toolbox talk" },
  training: { label: "Training video", noun: "training video" },
};

// Same cap as the server (SAFETY_VIDEO_MAX_MB) — checked up front so nobody
// waits on a 600 MB upload just to be told it's too big.
export const MAX_VIDEO_MB = 500;

const request = async (url, userId, { method = "GET", body } = {}) => {
  const res = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      "x-user-id": userId || "",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let content = {};
  try {
    content = await res.json();
  } catch (e) {
    // Non-JSON error page (e.g. the server is restarting).
  }
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

const enc = encodeURIComponent;

// ---- Overview (overview page + Safety homepage widgets)

export const fetchSafetyOverview = (userId) => request("/api/safety/overview", userId);

// ---- Safety concerns (read-only — reviewed in the app)

export const fetchSafetyConcerns = (userId) => request("/api/safety/concerns", userId);

// One concern with its photos (data URLs) and notes.
export const fetchSafetyConcern = (userId, id) =>
  request(`/api/safety/concerns/${enc(id)}`, userId);

// ---- Fire extinguishers (read-only — added and inspected in the app)

export const fetchExtinguishers = (userId) => request("/api/safety/extinguishers", userId);

export const fetchExtinguisherInspections = (userId, id) =>
  request(`/api/safety/extinguishers/${enc(id)}`, userId);

// The reference photo as an object URL (the auth header can't ride along on
// an <img src>). Callers revoke it. Resolves null when there's no photo.
export const fetchExtinguisherPhotoUrl = async (userId, id) => {
  const res = await fetch(`/api/safety/extinguishers/${enc(id)}/photo`, {
    headers: { "x-user-id": userId || "" },
  });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
};

// ---- Training videos

export const fetchTraining = (userId) => request("/api/safety/training", userId);

export const fetchTrainingVideo = (userId, id) =>
  request(`/api/safety/training/${enc(id)}`, userId);

// [{ role, people }] — roles people actually have (Admin left out).
export const fetchAssignableRoles = (userId) => request("/api/safety/training/roles", userId);

export const searchPeople = (userId, search) =>
  request(`/api/safety/training/people?search=${enc(search)}`, userId);

// { roles: [role], people: [email], dueAt: ISO | null }
// → { newlyAssigned, alreadyAssigned, total }
export const assignTraining = (userId, id, assignment) =>
  request(`/api/safety/training/${enc(id)}/assign`, userId, { method: "POST", body: assignment });

export const unassignTraining = (userId, id, assignmentId) =>
  request(`/api/safety/training/${enc(id)}/assignments/${enc(assignmentId)}`, userId, {
    method: "DELETE",
  });

// Stops auto-assigning the video to new members of a role. People already
// assigned keep it.
export const removeTrainingRole = (userId, id, role) =>
  request(`/api/safety/training/${enc(id)}/roles/${enc(role)}`, userId, { method: "DELETE" });

// ---- Toolbox talk results

export const fetchToolboxResults = (userId) => request("/api/safety/toolbox", userId);

export const fetchToolboxTalkResults = (userId, id) =>
  request(`/api/safety/toolbox/${enc(id)}`, userId);

// ---- Reports (PDF download / email) — server: safetyReports.js
//
// `kind` is one of extinguishers, concerns, training, training-video,
// toolbox, toolbox-talk. `selection` is what the page is showing: the ids of
// the visible rows in order (plus videoId / talkId / videoIds where the
// report needs them), the branch filter, and `scope`, a one-line description
// of the filters printed under the title.

// { title, scope, count, filename, subject, people: [{ name, email, branch, reason }] }
export const previewSafetyReport = (userId, kind, selection) =>
  request(`/api/safety/reports/${enc(kind)}/preview`, userId, { method: "POST", body: { selection } });

// { to: [email], subject, message, copyMe } → { sent: [email], cc }
export const emailSafetyReport = (userId, kind, selection, email) =>
  request(`/api/safety/reports/${enc(kind)}/email`, userId, { method: "POST", body: { selection, ...email } });

// The PDF itself → { blob, filename }.
export const fetchSafetyReportPdf = async (userId, kind, selection) => {
  const res = await fetch(`/api/safety/reports/${enc(kind)}/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-user-id": userId || "" },
    body: JSON.stringify({ selection }),
  });
  if (!res.ok) {
    let content = {};
    try {
      content = await res.json();
    } catch (e) {
      // Non-JSON error page.
    }
    throw new Error(content.error || "Couldn't create the PDF");
  }
  const filename = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1];
  return { blob: await res.blob(), filename: filename || "Safety_Report.pdf" };
};

// ---- Uploads

// Multipart upload of a toolbox talk or training video. Uses XHR rather than
// fetch so a big video can report progress: onProgress gets 0–1.
// Resolves with { id, kind, title, description, createdAt }.
export const uploadSafetyVideo = (userId, kind, { title, description, file }, onProgress) =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("title", title);
    form.append("description", description || "");
    form.append("video", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/safety/videos/${enc(kind)}`);
    xhr.setRequestHeader("x-user-id", userId || "");
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let content = {};
      try {
        content = JSON.parse(xhr.responseText);
      } catch (e) {
        // Non-JSON error page.
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(content);
      else reject(new Error(content.error || "Upload failed"));
    };
    xhr.onerror = () =>
      reject(new Error("The upload was interrupted — check your connection and try again."));
    xhr.send(form);
  });
